/**
 * Der Scanner: Handykamera und Bluetooth-Handscanner.
 *
 * Die Werte in diesem Modul sind **gemessen, nicht geraten**. Sie stammen aus
 * dem Inventur-Werkzeug des Ortsverbands, wo sie an echten Etiketten
 * eingestellt wurden (Stand 22.09.2026, ADR 0004). Wer sie ändert, sollte
 * vorher nachmessen.
 *
 * **Aufnehmen und Lesen sind getrennt.** Die Kamera liefert ein großes Bild,
 * gelesen wird aber nur der Zielrahmen in der Mitte. Ein Inventaretikett füllt
 * nur einen kleinen Fleck des Bildes; wer das ganze Bild dekodiert, gibt dem
 * DataMatrix-Code zu wenig Pixel -- genau daran scheiterte das Werkzeug, bis
 * es so umgebaut war. Der Ausschnitt gibt dem Code mehr Pixel und kostet
 * weniger Rechenzeit als vorher das ganze Bild.
 *
 * Erkannt wird über `BarcodeDetector`, siehe `erkenner.ts`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { einstellungen } from "../api/einstellungen.js";
import { erkennerBauart, erkennerVorbereiten, FORMATE } from "./erkenner.js";

/**
 * Derselbe Code laeuft bei Kamerabetrieb mehrmals pro Sekunde durch. Innerhalb
 * dieser Zeit ignorieren wir Wiederholungen, damit nicht jede Bewegung vor der
 * Linse eine Buchung ausloest.
 */
const WIEDERHOLSPERRE_MS = 2500;

/**
 * Pause zwischen zwei Erkennungsversuchen. Ohne sie saettigt die Erkennung
 * schwache Geraete. Zehn Versuche je Sekunde reichen, um ein Etikett zu
 * erwischen, und lassen dem Browser Luft fuer die Anzeige.
 */
const VERSUCHSPAUSE_MS = 100;

/**
 * So gross wie moeglich aufnehmen: Die Voreinstellung vieler Browser ist
 * 640x480, darin hat ein kleines Etikett zu wenig Pixel. Weil nur der
 * Ausschnitt gelesen wird, bleibt die Rechenzeit trotzdem im Rahmen.
 */
const KAMERA_BREITE = 1920;
const KAMERA_HOEHE = 1080;

/** Breite des gelesenen Bereichs, als Anteil am Bild. */
const ZIELRAHMEN_ANTEIL = 0.72;
/** Hoehe des gelesenen Bereichs, als Vielfaches seiner Breite. */
const ZIELRAHMEN_SEITEN = 0.72;

/**
 * Zoom beim Start. Zoom ist hier kein Komfort, sondern das wirksamste Mittel
 * gegen den eigentlichen Gegner, die Unschaerfe: Unter etwa 10 cm fokussiert
 * eine Handykamera nicht mehr, naeher herangehen macht es also schlechter.
 * Zoom macht den Code gross, ohne den Abstand zu verkuerzen.
 */
const ZOOM_START = 2;

/**
 * Ein Handscanner tippt wie eine Tastatur, nur viel schneller. Kommen die
 * Zeichen dichter als das, war es kein Mensch.
 */
const HID_HOECHSTABSTAND_MS = 40;
const HID_MINDESTLAENGE = 4;

export type ScanQuelle = "kamera" | "handscanner" | "tastatur";

/** Eingabearten, in die niemand tippt -- dort gehören die Tasten dem Scanner. */
const OHNE_TEXT = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color"]);

/** Nimmt dieses Element getippten Text an? */
function nimmtText(element: HTMLElement): boolean {
  if (element.isContentEditable) return true;
  if (element instanceof HTMLInputElement) return !OHNE_TEXT.has(element.type);
  return element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement;
}

export interface ScannerEigenschaften {
  /** Wird bei jedem erkannten Code gerufen. */
  aufCode: (code: string, quelle: ScanQuelle) => void;
  /** Kamera beim Oeffnen sofort starten. */
  sofortStarten?: boolean;
}

/**
 * Was eine Kamera ueber die Standardschnittstelle hinaus kann. Die
 * DOM-Typen kennen Zoom, Lampe und Fokus nicht -- sie stehen in einer
 * Erweiterung (Image Capture), die Chrome auf Android umsetzt.
 */
interface KameraFaehigkeiten {
  zoom?: { min: number; max: number; step?: number };
  torch?: boolean;
  focusMode?: string[];
}

interface Ausschnitt {
  x: number;
  y: number;
  breite: number;
  hoehe: number;
}

/** Der Bereich des Videobildes, der tatsaechlich gelesen wird. */
function ausschnitt(video: HTMLVideoElement): Ausschnitt {
  const breite = Math.round(video.videoWidth * ZIELRAHMEN_ANTEIL);
  const hoehe = Math.min(Math.round(breite * ZIELRAHMEN_SEITEN), video.videoHeight);
  return {
    x: Math.round((video.videoWidth - breite) / 2),
    y: Math.round((video.videoHeight - hoehe) / 2),
    breite,
    hoehe,
  };
}

function faehigkeiten(spur: MediaStreamTrack | null): KameraFaehigkeiten {
  try {
    return (spur?.getCapabilities() ?? {}) as KameraFaehigkeiten;
  } catch {
    return {};
  }
}

/**
 * Wartet, bis das Bild seine Masse kennt -- ohne die ist kein Ausschnitt
 * berechenbar. Nach der Frist geht es trotzdem weiter: Die Schleife prueft bei
 * jedem Bild erneut.
 */
function aufBildWarten(video: HTMLVideoElement, fristMs = 4000): Promise<void> {
  if (video.videoWidth > 0) return Promise.resolve();
  return new Promise((fertig) => {
    const uhr = setTimeout(fertig, fristMs);
    video.addEventListener(
      "loadedmetadata",
      () => {
        clearTimeout(uhr);
        fertig();
      },
      { once: true },
    );
  });
}

/**
 * Dauerautofokus anfordern, soweit das Geraet ihn kann und noch nicht nutzt.
 *
 * Nur eingreifen, wenn es wirklich etwas aendert: `applyConstraints` auf einer
 * laufenden Spur bringt manche Android-Kameras aus dem Tritt, und die meisten
 * fokussieren von sich aus schon dauerhaft.
 */
async function fokusEinstellen(spur: MediaStreamTrack): Promise<void> {
  const kann = faehigkeiten(spur);
  const ist = spur.getSettings() as { focusMode?: string };
  if (
    kann.focusMode?.includes("continuous") === true &&
    ist.focusMode !== undefined &&
    ist.focusMode !== "continuous"
  ) {
    try {
      await spur.applyConstraints({
        advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
      });
    } catch {
      // Dann eben mit dem Fokus, den das Geraet von sich aus waehlt.
    }
  }
}

/** Zoomwert fuer die Anzeige: „2,0×". */
function zoomText(wert: number): string {
  return `${wert.toFixed(1).replace(".", ",")}×`;
}

/**
 * Ton und Vibration nach einem Scan (CLAUDE.md §7).
 *
 * „Man muss ohne Hinsehen wissen, ob es geklappt hat." Deshalb beides, und
 * deshalb unterschiedlich hoch: hoch und kurz für gut, tief und lang für
 * schlecht.
 */
export function rueckmeldungGeben(gut: boolean): void {
  const { ton, vibration } = einstellungen();
  if (vibration && "vibrate" in navigator) navigator.vibrate(gut ? 40 : [60, 50, 60]);
  if (!ton) return;
  try {
    const kontext = new AudioContext();
    const ton = kontext.createOscillator();
    const lautstaerke = kontext.createGain();
    ton.frequency.value = gut ? 880 : 320;
    lautstaerke.gain.value = 0.06;
    ton.connect(lautstaerke).connect(kontext.destination);
    ton.start();
    ton.stop(kontext.currentTime + (gut ? 0.09 : 0.22));
    ton.onended = () => void kontext.close();
  } catch {
    // Ton ist Beiwerk, kein Grund zum Abbruch.
  }
}

export function Scanner({ aufCode, sofortStarten = true }: ScannerEigenschaften) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const spurRef = useRef<MediaStreamTrack | null>(null);
  const zuletztRef = useRef<{ code: string; zeit: number }>({ code: "", zeit: 0 });
  const zaehlerRef = useRef({ versuche: 0, treffer: 0 });

  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [bildMasse, setBildMasse] = useState<{ breite: number; hoehe: number } | null>(null);
  const [diagnose, setDiagnose] = useState<string | null>(null);
  const [lampeAn, setLampeAn] = useState(false);
  const [lampeMoeglich, setLampeMoeglich] = useState(false);
  const [zoomGrenzen, setZoomGrenzen] = useState<NonNullable<KameraFaehigkeiten["zoom"]> | null>(
    null,
  );
  const [zoom, setZoom] = useState(ZOOM_START);
  const [kameras, setKameras] = useState<MediaDeviceInfo[]>([]);
  const [gewaehlteKamera, setGewaehlteKamera] = useState<string>("");

  /** Ein Code, der gerade erst kam, wird nicht noch einmal gemeldet. */
  const codeMelden = useCallback(
    (code: string, quelle: ScanQuelle) => {
      const jetzt = Date.now();
      const zuletzt = zuletztRef.current;
      if (code === zuletzt.code && jetzt - zuletzt.zeit < WIEDERHOLSPERRE_MS) return;
      zuletztRef.current = { code, zeit: jetzt };
      aufCode(code, quelle);
    },
    [aufCode],
  );

  // --- Bluetooth-Handscanner ------------------------------------------------
  //
  // Er meldet sich als Tastatur. Erkannt wird er daran, dass die Zeichen
  // ungewoehnlich schnell kommen und mit Enter enden. Das laeuft immer mit --
  // auch wenn die Kamera aus ist, und ohne dass ein Feld den Fokus haelt.
  useEffect(() => {
    let puffer = "";
    let letztes = 0;

    const aufTaste = (e: KeyboardEvent): void => {
      // In einem Textfeld hat der Benutzer das Wort, nicht der Scanner. Ein
      // Schalter oder Kästchen dagegen nicht: Sonst verschluckte der Schalter
      // „… und als Stammplatz festlegen“ nach dem Antippen jeden Scan.
      const ziel = e.target as HTMLElement | null;
      if (ziel !== null && nimmtText(ziel)) return;

      const jetzt = Date.now();
      if (jetzt - letztes > HID_HOECHSTABSTAND_MS) puffer = "";
      letztes = jetzt;

      if (e.key === "Enter") {
        if (puffer.length >= HID_MINDESTLAENGE) {
          // Das Enter gehoert dem Scanner. Sonst loest es den Knopf aus, der
          // gerade den Fokus hat -- etwa „Einbuchen“ -- und beendet den
          // Dauerscan mitten im Packen.
          e.preventDefault();
          codeMelden(puffer, "handscanner");
        }
        puffer = "";
        return;
      }
      if (e.key.length === 1) puffer += e.key;
    };

    globalThis.addEventListener("keydown", aufTaste);
    return () => {
      globalThis.removeEventListener("keydown", aufTaste);
    };
  }, [codeMelden]);

  // --- Kamera ---------------------------------------------------------------

  const kameraStoppen = useCallback(() => {
    const video = videoRef.current;
    const strom = video?.srcObject as MediaStream | null;
    strom?.getTracks().forEach((t) => {
      t.stop();
    });
    if (video !== null) video.srcObject = null;
    spurRef.current = null;
    setLaeuft(false);
    setLampeAn(false);
    setLampeMoeglich(false);
    setZoomGrenzen(null);
    setBildMasse(null);
    setDiagnose(null);
  }, []);

  const zoomen = useCallback(async (wunsch: number): Promise<void> => {
    const spur = spurRef.current;
    const grenzen = faehigkeiten(spur).zoom;
    if (spur === null || grenzen === undefined) return;
    const wert = Math.min(grenzen.max, Math.max(grenzen.min, wunsch));
    setZoom(wert);
    try {
      await spur.applyConstraints({ advanced: [{ zoom: wert } as MediaTrackConstraintSet] });
    } catch {
      // Manche Geraete melden Zoom und nehmen ihn dann doch nicht an.
    }
  }, []);

  const kameraStarten = useCallback(
    async (geraeteId?: string) => {
      if (!globalThis.isSecureContext) {
        setMeldung(
          "Der Browser gibt die Kamera nur über HTTPS oder localhost frei. " +
            "Der Handscanner funktioniert trotzdem.",
        );
        return;
      }

      try {
        const welche: MediaTrackConstraints =
          geraeteId === undefined
            ? { facingMode: { ideal: "environment" } }
            : { deviceId: { exact: geraeteId } };

        let strom: MediaStream;
        try {
          strom = await navigator.mediaDevices.getUserMedia({
            video: { ...welche, width: { ideal: KAMERA_BREITE }, height: { ideal: KAMERA_HOEHE } },
          });
        } catch (fehler) {
          if ((fehler as DOMException).name !== "OverconstrainedError") throw fehler;
          // Das Geraet kann die Wunschaufloesung nicht -- dann eben seine
          // eigene. Aber weiterhin die Kamera auf der Rueckseite.
          strom = await navigator.mediaDevices.getUserMedia({ video: welche });
        }

        const video = videoRef.current;
        if (video === null) {
          strom.getTracks().forEach((t) => {
            t.stop();
          });
          return;
        }
        video.srcObject = strom;
        // play() **nicht** abwarten: Unter einer Autoplay-Sperre oder im
        // Hintergrundtab loest das Versprechen nie auf, und der Start haengt.
        // Ob Bilder ankommen, sagt ohnehin erst videoWidth.
        video.play().catch(() => {
          // Die Bilder kommen meist trotzdem.
        });
        await aufBildWarten(video);

        const spur = strom.getVideoTracks()[0] ?? null;
        spurRef.current = spur;
        if (spur !== null) await fokusEinstellen(spur);

        const kann = faehigkeiten(spur);
        setLampeMoeglich(kann.torch === true);
        setZoomGrenzen(kann.zoom ?? null);
        if (kann.zoom !== undefined) await zoomen(ZOOM_START);

        setBildMasse(
          video.videoWidth > 0 ? { breite: video.videoWidth, hoehe: video.videoHeight } : null,
        );
        zaehlerRef.current = { versuche: 0, treffer: 0 };
        setLaeuft(true);
        setMeldung(null);

        // Geraetenamen liefert der Browser erst nach erteilter Freigabe.
        const geraete = (await navigator.mediaDevices.enumerateDevices()).filter(
          (g) => g.kind === "videoinput",
        );
        setKameras(geraete);
        setGewaehlteKamera(spur?.getSettings().deviceId ?? "");
      } catch (fehler) {
        const name = (fehler as DOMException).name;
        setMeldung(
          name === "NotAllowedError"
            ? "Der Browser hat den Zugriff abgelehnt. In den Seiteneinstellungen die Kamera erlauben und erneut versuchen."
            : name === "NotFoundError"
              ? "Es ist keine Kamera angeschlossen."
              : name === "NotReadableError"
                ? "Die Kamera wird schon von einem anderen Programm benutzt."
                : `Die Kamera lässt sich nicht öffnen: ${(fehler as Error).message}`,
        );
        kameraStoppen();
      }
    },
    [kameraStoppen, zoomen],
  );

  /**
   * Dreht jemand das Handy, tauschen Breite und Hoehe des Bildes. Rahmen und
   * Ausschnitt muessen mitgehen, sonst liest die Schleife woanders, als der
   * Rahmen zeigt.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!laeuft || video === null) return;
    const neu = (): void => {
      if (video.videoWidth > 0) {
        setBildMasse({ breite: video.videoWidth, hoehe: video.videoHeight });
      }
    };
    video.addEventListener("resize", neu);
    video.addEventListener("loadedmetadata", neu);
    return () => {
      video.removeEventListener("resize", neu);
      video.removeEventListener("loadedmetadata", neu);
    };
  }, [laeuft]);

  /**
   * Die Erkennungsschleife. Laeuft, solange die Kamera laeuft.
   *
   * Je Durchgang wird der Zielrahmen auf eine Leinwand kopiert und nur die
   * gelesen -- nicht das ganze Bild.
   */
  useEffect(() => {
    if (!laeuft) return;

    let weiter = true;
    const leinwand = document.createElement("canvas");
    // willReadFrequently: ohne das liest Chrome den Bildspeicher jedes Mal
    // langsam von der Grafikkarte zurueck.
    const pinsel = leinwand.getContext("2d", { willReadFrequently: true });

    const schleife = async (): Promise<void> => {
      const Bauart = await erkennerBauart();
      const erkenner = new Bauart({ formats: FORMATE });

      while (weiter) {
        const video = videoRef.current;
        if (pinsel !== null && video !== null && video.videoWidth > 0) {
          const a = ausschnitt(video);
          if (leinwand.width !== a.breite || leinwand.height !== a.hoehe) {
            leinwand.width = a.breite;
            leinwand.height = a.hoehe;
          }
          pinsel.drawImage(video, a.x, a.y, a.breite, a.hoehe, 0, 0, a.breite, a.hoehe);
          zaehlerRef.current.versuche += 1;
          try {
            const treffer = await erkenner.detect(leinwand);
            const erster = treffer[0];
            if (erster !== undefined && erster.rawValue !== "") {
              zaehlerRef.current.treffer += 1;
              codeMelden(erster.rawValue, "kamera");
            }
          } catch {
            // Ein Bild ohne Code ist der Normalfall und kein Grund zur Meldung.
          }
        }
        await new Promise((fertig) => setTimeout(fertig, VERSUCHSPAUSE_MS));
      }
    };

    void schleife();
    return () => {
      weiter = false;
    };
  }, [laeuft, codeMelden]);

  /**
   * Eine kleine Zeile im Kamerabild: was wirklich ankommt. Ob es an der
   * Kamera, am Licht oder am Etikett liegt, laesst sich von aussen nicht
   * unterscheiden -- diese Zeile macht aus „geht nicht" eine Diagnose.
   */
  useEffect(() => {
    if (!laeuft) return;
    const takt = setInterval(() => {
      const video = videoRef.current;
      const z = zaehlerRef.current;
      const versuche = z.versuche;
      z.versuche = 0;
      if (video === null || video.videoWidth === 0) {
        setDiagnose("Kamera startet …");
        return;
      }
      const a = ausschnitt(video);
      setDiagnose(
        `${String(video.videoWidth)}×${String(video.videoHeight)} · liest ` +
          `${String(a.breite)}×${String(a.hoehe)} · ${String(versuche)}/s · ` +
          `${String(z.treffer)} Treffer`,
      );
    }, 1000);
    return () => {
      clearInterval(takt);
    };
  }, [laeuft]);

  useEffect(() => {
    // Die Erkennung schon anstossen, waehrend der Benutzer noch die Kamera
    // freigibt -- so ist sie da, wenn das erste Bild kommt.
    erkennerVorbereiten();
    if (sofortStarten) void kameraStarten();
    return kameraStoppen;
  }, [sofortStarten, kameraStarten, kameraStoppen]);

  /**
   * Im Lager ist schlechtes Licht der haeufigste Grund, warum ein Code nicht
   * gelesen wird. Nicht jedes Geraet kann die Lampe steuern -- der Knopf
   * erscheint nur, wenn es geht.
   */
  const lampeSchalten = async (): Promise<void> => {
    const spur = spurRef.current;
    if (spur === null) return;
    const neu = !lampeAn;
    try {
      await spur.applyConstraints({ advanced: [{ torch: neu } as MediaTrackConstraintSet] });
      setLampeAn(neu);
    } catch {
      setLampeMoeglich(false);
      setMeldung("Dieses Gerät gibt die Lampe nicht frei.");
    }
  };

  // Der weisse Rahmen liegt **genau** ueber dem Bereich, der gelesen wird.
  // Dafuer nimmt die Vorschau das Seitenverhaeltnis der Kamera an und zeigt
  // das ganze Bild (object-contain). Mit einem abgeschnittenen Bild und einem
  // fest aufgemalten Rahmen log der Rahmen: Wer den Code sauber hineinhielt,
  // ging trotzdem leer aus -- und wusste nicht, warum.
  const rahmen =
    bildMasse === null
      ? null
      : (() => {
          const breite = bildMasse.breite * ZIELRAHMEN_ANTEIL;
          const hoehe = Math.min(breite * ZIELRAHMEN_SEITEN, bildMasse.hoehe);
          const links = (100 * (bildMasse.breite - breite)) / 2 / bildMasse.breite;
          const oben = (100 * (bildMasse.hoehe - hoehe)) / 2 / bildMasse.hoehe;
          return { links, oben };
        })();

  return (
    <div className="flex flex-col gap-3">
      <div
        className="relative mx-auto w-full overflow-hidden rounded-xl bg-black"
        style={
          bildMasse === null
            ? undefined
            : {
                aspectRatio: `${String(bildMasse.breite)} / ${String(bildMasse.hoehe)}`,
                // Im Hochformat nimmt die Vorschau sonst die ganze Seite ein
                // und schiebt die Knoepfe darunter aus dem Bild. Begrenzt wird
                // ueber die Breite, damit das Seitenverhaeltnis stimmt.
                maxWidth: `calc(58vh * ${String(bildMasse.breite)} / ${String(bildMasse.hoehe)})`,
              }
        }
      >
        <video
          ref={videoRef}
          className={`h-full w-full object-contain ${laeuft ? "" : "hidden"}`}
          playsInline
          muted
          aria-label="Kamerabild"
        />
        {laeuft && rahmen !== null && (
          <div
            className="pointer-events-none absolute rounded-xl border-4 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.28)]"
            style={{
              left: `${String(rahmen.links)}%`,
              right: `${String(rahmen.links)}%`,
              top: `${String(rahmen.oben)}%`,
              bottom: `${String(rahmen.oben)}%`,
            }}
          />
        )}
        {laeuft && diagnose !== null && (
          <p className="pointer-events-none absolute bottom-1 left-2 font-mono text-xs text-white/80">
            {diagnose}
          </p>
        )}
        {!laeuft && (
          <div className="flex min-h-40 items-center justify-center p-6 text-center text-sm text-neutral-300">
            Kamera aus. Der Handscanner funktioniert trotzdem.
          </div>
        )}
      </div>

      {laeuft && (
        <p className="text-center text-sm text-neutral-600 dark:text-neutral-300">
          Code in den weißen Rahmen halten. Nicht zu nah heran — lieber zoomen.
        </p>
      )}

      {meldung !== null && (
        <p className="rounded-lg bg-amber-100 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
          {meldung}
        </p>
      )}

      {laeuft && zoomGrenzen !== null && (
        <label className="flex min-h-12 items-center gap-3 rounded-xl bg-white px-4 dark:bg-neutral-800">
          <span className="text-sm font-semibold">Zoom</span>
          <input
            type="range"
            className="h-12 flex-1 accent-blue-700"
            min={zoomGrenzen.min}
            max={zoomGrenzen.max}
            step={zoomGrenzen.step ?? 0.1}
            value={zoom}
            onChange={(e) => void zoomen(Number(e.target.value))}
          />
          <span className="min-w-12 text-right font-mono text-sm tabular-nums">
            {zoomText(zoom)}
          </span>
        </label>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="min-h-12 flex-1 rounded-xl bg-neutral-800 px-4 text-base font-semibold text-white active:bg-neutral-700 dark:bg-neutral-200 dark:text-neutral-900"
          onClick={() => {
            if (laeuft) kameraStoppen();
            else void kameraStarten();
          }}
        >
          {laeuft ? "Kamera stoppen" : "Kamera starten"}
        </button>

        {lampeMoeglich && (
          <button
            type="button"
            aria-pressed={lampeAn}
            className={`min-h-12 rounded-xl px-4 text-base font-semibold ${
              lampeAn
                ? "bg-amber-400 text-neutral-900"
                : "bg-neutral-200 dark:bg-neutral-700 dark:text-white"
            }`}
            onClick={() => void lampeSchalten()}
          >
            Licht
          </button>
        )}
      </div>

      {kameras.length > 1 && (
        <select
          className="min-h-12 rounded-xl border border-neutral-300 bg-white px-3 text-base dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
          value={gewaehlteKamera}
          onChange={(e) => {
            setGewaehlteKamera(e.target.value);
            kameraStoppen();
            void kameraStarten(e.target.value);
          }}
          aria-label="Kamera wählen"
        >
          {kameras.map((k, i) => (
            <option key={k.deviceId} value={k.deviceId}>
              {k.label === "" ? `Kamera ${String(i + 1)}` : k.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
